import React, { useRef, useState } from 'react';
import { CloseOutlined } from '@ant-design/icons';
// import useSentryContext from '../utils/sentryContext';

const defaultAlertContextValue = {
    alertOptions: {
        status: false,
        type: '',
        message: '',
        description: '',
    },
    set_alertOptions: () => {},
    alerts: {
        status: false,
        type: '',
        message: '',
        description: '',
    },
    setAlerts: () => {},
    handleAlerts: () => {},
    closeAlert: () => {},
    CloseOutlined,
};

const AlertContext = React.createContext(defaultAlertContextValue);

// `toggleAlert("error", "Error", err)` is the house style in ~160 places, and on
// a rejected request `err` is the raw axios Error — an object. Topbar renders
// alertOptions.description straight into antd's Alert, so one of those puts an
// object where React expects a child and white-screens the whole page
// ("Objects are not valid as a React child (found: object with keys {message,
// name, code, config, request, response, status})").
//
// Fixing 160 call sites is neither safe nor necessary: every one of them funnels
// through set_alertOptions, so coerce here instead. Strings, numbers and real
// elements pass through untouched; anything else is reduced to the most useful
// message we can find on it.
const toRenderableText = (value) => {
    if (value === null || value === undefined) return value;
    if (typeof value === 'string' || typeof value === 'number') return value;
    if (React.isValidElement(value)) return value;
    if (Array.isArray(value)) return value.map(toRenderableText);

    const candidates = [
        value?.response?.data?.error_details,
        value?.response?.data?.detail,
        value?.response?.data?.message,
        value?.detail,
        value?.message,
    ];
    for (const candidate of candidates) {
        if (typeof candidate === 'string' && candidate.trim()) return candidate;
        if (Array.isArray(candidate) && typeof candidate[0] === 'string') return candidate[0];
    }
    return 'Something went wrong. Please try again.';
};

const sanitizeAlert = (next) => (
    next && typeof next === 'object'
        ? { ...next, message: toRenderableText(next.message), description: toRenderableText(next.description) }
        : next
);

const AlertProvider = ({ children }) => {
    const [alertOptions, _set_alertOptions] = useState({
        status: false,
        type: "success",
        message: "Success",
        description: "Invited Successfully",
    });
    
    // Same setter signature as useState (value or updater), sanitized on the way in.
    const set_alertOptions = React.useCallback((value) => {
        _set_alertOptions((prev) =>
            sanitizeAlert(typeof value === 'function' ? value(prev) : value)
        );
    }, []);

    const [alerts, _setAlerts] = useState({
        status: false,
        type: "success",
        message: "Success",
        description: "Invited Successfully",
    });

    const setAlerts = React.useCallback((value) => {
        _setAlerts((prev) =>
            sanitizeAlert(typeof value === 'function' ? value(prev) : value)
        );
    }, []);

    const autoCloseTimeoutRef = useRef(null);

    // const snapshot = useMemo(() => ({
    //     alertOptions,
    //     alerts,
    // }), [alertOptions, alerts]);
    // useSentryContext("alertContext", snapshot);

    const handleAlerts = (type, message, description) => {
        if (autoCloseTimeoutRef.current) {
            clearTimeout(autoCloseTimeoutRef.current);
            autoCloseTimeoutRef.current = null;
        }

        setAlerts({
            status: true,
            type: type,
            message: message,
            description: description,
        });

        autoCloseTimeoutRef.current = setTimeout(() => {
            setAlerts(prev => ({
                ...prev,
                status: false,
            }));
            autoCloseTimeoutRef.current = null;
        }, 5 * 1000);
    }

    const closeAlert = () => {
        if (autoCloseTimeoutRef.current) {
            clearTimeout(autoCloseTimeoutRef.current);
            autoCloseTimeoutRef.current = null;
        }
        set_alertOptions(prev => ({
            ...prev,
            status: false,
        }));
        setAlerts(prev => ({
            ...prev,
            status: false,
        }));
    }

    return (
        <AlertContext.Provider value={{alertOptions, set_alertOptions, alerts, setAlerts, handleAlerts, closeAlert, CloseOutlined }}>
            {children}
        </AlertContext.Provider>
    );
}

export { AlertContext, AlertProvider };